import type { TrialBalanceRow } from '@erp/shared';

import { api } from './api.js';
import { formatMoney } from './money.js';

/** One account's current balance: in base, and per transaction currency. */
export interface AccountBalance {
  base: string;
  byCurrency: Array<{ currencyCode: string; balance: string }>;
}
export type AccountBalanceMap = Map<string, AccountBalance>;

/**
 * Every account's current balance, from the trial balance. The trial balance
 * lists only accounts that have moved, so an absent account is zero.
 */
export async function loadAccountBalances(): Promise<AccountBalanceMap> {
  const r = await api().get<{ items: TrialBalanceRow[] }>('/general-ledger/trial-balance');
  return new Map(r.data.items.map((row) => [row.accountId, {
    base: row.baseBalance,
    byCurrency: (row.byCurrency ?? []).map((c) => ({ currencyCode: c.currencyCode, balance: c.balance })),
  }]));
}

/**
 * An account's balance as the pickers print it — in EVERY currency it holds:
 * a cash box with dinars and dollars reads "IQD 1,200,000 · $9,858.15", not one
 * base figure that hides the dollars. Base first; zero shares are left out; an
 * account that never moved reads zero in base.
 */
export function formatAccountBalance(b: AccountBalance | undefined, locale: string, baseCurrency: string): string {
  const parts = (b?.byCurrency ?? [])
    .filter((c) => Math.abs(Number(c.balance)) > 0.00005)
    .sort((x, y) => (x.currencyCode === baseCurrency ? -1 : y.currencyCode === baseCurrency ? 1 : x.currencyCode.localeCompare(y.currencyCode)))
    .map((c) => formatMoney(c.balance, { locale, currency: c.currencyCode }));
  if (parts.length === 0) return formatMoney(b?.base ?? '0', { locale, currency: baseCurrency });
  return parts.join(' · ');
}
